import { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { ShoppingCart, Plus, Minus, Trash2 } from 'lucide-react'
import { useTelegram } from '@/shared/lib/telegram'
import { formatPrice, formatWeightShort } from '@/shared/lib/format'
import { calculateTeaPrice, CAKE_WEIGHT } from '@/shared/lib/pricing'
import { cn } from '@/shared/lib/utils'

export interface ProductActionBarProps {
  /**
   * Количество товара в корзине
   */
  cartQuantity?: number
  /**
   * Цена за 1 г (для чая, используется для расчетов) или цена товара (для посуды)
   * Примечание: для отображения цена умножается на 10 для чая
   */
  price: number
  /**
   * Тип товара (для отображения "за 10г" только для чая)
   */
  productType?: 'tea' | 'teaware'
  /**
   * Товар в корзине
   */
  inCart?: boolean
  /**
   * Текущий вес в корзине (для чая)
   */
  currentWeight?: number
  /**
   * Цена за блин если доступна
   */
  cakePriceAmount?: number | null
  /**
   * Пользовательский вес блина (null = 357г)
   */
  cakeWeight?: number | null
  /**
   * Только блин (без вариантов 10/50/100г)
   */
  isCakeOnly?: boolean
  /**
   * Состояние загрузки
   */
  loading?: boolean
  /**
   * Обработчик добавления в корзину
   */
  onAddToCart: () => void
  /**
   * Обработчик изменения количества (для quick-edit)
   */
  onQuantityChange?: (delta: number) => void
}

/**
 * Виджет: Фиксированный CTA-блок снизу экрана
 * 
 * Показывается в 100% случаев, фиксированно на нижней части экрана.
 * При добавлении в корзину показывает quick-edit с кнопками − / +
 */
export function ProductActionBar({
  cartQuantity = 0,
  price,
  productType = 'tea',
  inCart = false,
  currentWeight,
  cakePriceAmount,
  cakeWeight,
  isCakeOnly,
  loading = false,
  onAddToCart,
  onQuantityChange,
}: ProductActionBarProps) {
  const { hapticFeedback, isVk } = useTelegram()
  const isTea = productType === 'tea'
  const [isUpdating, setIsUpdating] = useState(false)
  
  // Расчёт цены через централизованную функцию
  const totalPrice = isTea && currentWeight
    ? calculateTeaPrice({
        pricePerGram: price,
        weight: currentWeight,
        quantity: cartQuantity,
        cakePriceAmount,
        cakeWeight,
      }).subtotal
    : price * (cartQuantity || 1)
  
  const handleAddToCart = () => {
    hapticFeedback.impactOccurred('medium')
    onAddToCart()
  }
  
  const handleDecrease = async () => {
    if (cartQuantity <= 0 || !onQuantityChange) return
    setIsUpdating(true)
    hapticFeedback.selectionChanged()
    await onQuantityChange(-1)
    setIsUpdating(false)
  }
  
  const handleIncrease = async () => {
    if (!onQuantityChange) return
    setIsUpdating(true)
    hapticFeedback.selectionChanged()
    await onQuantityChange(1)
    setIsUpdating(false)
  }
  
  return (
    <div
      className={cn(
        "fixed left-0 right-0 z-50 bg-paper dark:bg-[#0F1113] border-t border-silk dark:border-dark-border [bottom:calc(64px+var(--safe-area-inset-bottom))] md:bottom-0",
        !isVk && "md:left-[220px] lg:left-[240px]",
      )}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div className="px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Информация о цене */}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-bold text-primary dark:text-primary-light">
                {formatPrice(
                  inCart && isTea
                    ? totalPrice
                    : isTea
                      ? isCakeOnly
                        ? calculateTeaPrice({ pricePerGram: price, weight: cakeWeight ?? CAKE_WEIGHT, quantity: 1, cakePriceAmount, cakeWeight }).unitPrice
                        : price * 10
                      : price
                )}
              </span>
              {isTea && !inCart && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {isCakeOnly ? `за блин` : 'за 10г'}
                </span>
              )}
              {isTea && inCart && currentWeight && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {formatWeightShort(currentWeight)} × {cartQuantity}
                </span>
              )}
            </div>
          </div>
          
          {/* Кнопка добавления в корзину / Quick-edit */}
          <AnimatePresence mode="wait">
            {inCart && cartQuantity > 0 ? (
              <motion.div
                key="quick-edit"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="flex items-center gap-1"
              >
                {/* Кнопка уменьшения / удаления */}
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={handleDecrease}
                  disabled={isUpdating}
                  className={cn(
                    'w-11 h-11 rounded-xl flex items-center justify-center',
                    'border-2 transition-all',
                    cartQuantity === 1
                      ? 'bg-error/10 dark:bg-error/20 border-error/30 dark:border-error/40 text-error'
                      : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-ink dark:text-white',
                    'active:scale-95 disabled:opacity-50'
                  )}
                  aria-label={cartQuantity === 1 ? 'Удалить из корзины' : 'Уменьшить количество'}
                >
                  {cartQuantity === 1 ? (
                    <Trash2 className="h-5 w-5" />
                  ) : (
                    <Minus className="h-5 w-5" />
                  )}
                </motion.button>
                
                {/* Количество */}
                <motion.div
                  key={cartQuantity}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className={cn(
                    'w-12 h-11 flex items-center justify-center',
                    'bg-primary/10 dark:bg-primary/20 rounded-xl',
                    'text-lg font-bold text-primary dark:text-primary-light'
                  )}
                >
                  {cartQuantity}
                </motion.div>
                
                {/* Кнопка увеличения */}
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={handleIncrease}
                  disabled={isUpdating}
                  className={cn(
                    'w-11 h-11 rounded-xl flex items-center justify-center',
                    'bg-primary dark:bg-primary-dark text-white',
                    'active:scale-95 disabled:opacity-50 transition-all'
                  )}
                  aria-label="Увеличить количество"
                >
                  <Plus className="h-5 w-5" />
                </motion.button>
              </motion.div>
            ) : (
              <motion.button
                key="add-to-cart"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAddToCart}
                disabled={loading}
                className={cn(
                  'px-8 py-3 bg-primary dark:bg-primary-dark text-white rounded-2xl font-medium',
                  'hover:bg-primary-dark dark:hover:bg-primary-darker transition-all',
                  'touch-target disabled:opacity-50 disabled:cursor-not-allowed',
                  'flex items-center gap-2'
                )}
              >
                <ShoppingCart className="h-5 w-5" />
                <span>{loading ? 'Добавление...' : 'В корзину'}</span>
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
